import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  FiCalendar,
  FiMapPin,
  FiUsers,
  FiCheck,
  FiX,
  FiEye,
  FiDollarSign,
  FiClock,
  FiTrendingUp,
  FiSearch,
  FiFilter,
  FiMessageSquare
} from 'react-icons/fi';
import { PropertyThumbnail } from '../components/OptimizedImage';
import './HostReservations.css';

const HostReservations = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending'); // pending, active, upcoming, past
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [sortBy, setSortBy] = useState('check_in'); // check_in, created_at, total_price

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, propertiesRes] = await Promise.all([
        api.get('/api/bookings?type=host'),
        api.get(`/api/users/${user.id}/properties`)
      ]);
      setBookings(bookingsRes.data.bookings);
      setProperties(propertiesRes.data.properties);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('hostReservations.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmBooking = async (bookingId) => {
    if (!window.confirm(t('hostReservations.confirmBookingPrompt'))) return;

    try {
      await api.post(`/api/bookings/${bookingId}/confirm`);
      toast.success(t('hostReservations.bookingConfirmed'));
      fetchData();
    } catch (error) {
      toast.error(t('hostReservations.failedToConfirm'));
    }
  };

  const handleRejectBooking = async (bookingId) => {
    const reason = window.prompt(t('hostReservations.rejectReason'));
    if (!reason) return;

    try {
      await api.post(`/api/bookings/${bookingId}/cancel`, { reason });
      toast.success(t('hostReservations.bookingRejected'));
      fetchData();
    } catch (error) {
      toast.error(t('hostReservations.failedToReject'));
    }
  };

  const handleCompleteBooking = async (bookingId) => {
    if (!window.confirm(t('hostReservations.completeBookingPrompt'))) return;

    try {
      await api.post(`/api/bookings/${bookingId}/complete`);
      toast.success(t('hostReservations.bookingCompleted'));
      fetchData();
    } catch (error) {
      toast.error(t('hostReservations.failedToComplete'));
    }
  };

  const handleCancelBooking = async (bookingId) => {
    const reason = window.prompt(t('hostReservations.cancelReason'));
    if (!reason) return;

    try {
      await api.post(`/api/bookings/${bookingId}/cancel`, { reason });
      toast.success(t('hostReservations.bookingCancelled'));
      fetchData();
    } catch (error) {
      toast.error(t('hostReservations.failedToCancel'));
    }
  };

  const categorizeBookings = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    return {
      pending: bookings.filter(b => b.status === 'pending'),
      active: bookings.filter(b => {
        const checkIn = new Date(b.check_in);
        const checkOut = new Date(b.check_out);
        checkIn.setHours(0, 0, 0, 0);
        checkOut.setHours(0, 0, 0, 0);
        return b.status === 'confirmed' && checkIn <= now && checkOut >= now;
      }),
      upcoming: bookings.filter(b => {
        const checkIn = new Date(b.check_in);
        checkIn.setHours(0, 0, 0, 0);
        return b.status === 'confirmed' && checkIn > now;
      }),
      past: bookings.filter(b =>
        b.status === 'completed' ||
        b.status === 'cancelled' ||
        (b.status === 'confirmed' && new Date(b.check_out) < now)
      )
    };
  };

  const calculateStats = () => {
    const categorized = categorizeBookings();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const checkInsToday = bookings.filter(b => {
      const checkIn = new Date(b.check_in);
      return checkIn >= today && checkIn < tomorrow && b.status === 'confirmed';
    }).length;

    const checkOutsToday = bookings.filter(b => {
      const checkOut = new Date(b.check_out);
      return checkOut >= today && checkOut < tomorrow && b.status === 'confirmed';
    }).length;

    // Revenue this month
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthlyRevenue = bookings
      .filter(b => {
        const checkIn = new Date(b.check_in);
        return checkIn >= firstDayOfMonth && b.status !== 'cancelled';
      })
      .reduce((sum, b) => sum + parseFloat(b.total_price || 0), 0);

    // Occupancy rate (last 30 days)
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const totalPropertyNights = properties.length * 30;
    const bookedNights = bookings
      .filter(b => {
        const checkIn = new Date(b.check_in);
        const checkOut = new Date(b.check_out);
        return (checkIn >= thirtyDaysAgo || checkOut >= thirtyDaysAgo) && b.status !== 'cancelled';
      })
      .reduce((sum, b) => {
        const checkIn = new Date(b.check_in);
        const checkOut = new Date(b.check_out);
        const start = checkIn < thirtyDaysAgo ? thirtyDaysAgo : checkIn;
        const end = checkOut > now ? now : checkOut;
        const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        return sum + Math.max(0, nights);
      }, 0);

    const occupancyRate = totalPropertyNights > 0 ? (bookedNights / totalPropertyNights * 100) : 0;

    return {
      pending: categorized.pending.length,
      checkInsToday,
      checkOutsToday,
      monthlyRevenue,
      occupancyRate: occupancyRate.toFixed(0)
    };
  };

  const filterAndSortBookings = (bookingsList) => {
    let filtered = bookingsList;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(b =>
        b.guest?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.guest?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.property?.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by property
    if (selectedProperty !== 'all') {
      filtered = filtered.filter(b => b.property_id === parseInt(selectedProperty));
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'check_in') {
        return new Date(a.check_in) - new Date(b.check_in);
      } else if (sortBy === 'created_at') {
        return new Date(b.created_at) - new Date(a.created_at);
      } else if (sortBy === 'total_price') {
        return parseFloat(b.total_price) - parseFloat(a.total_price);
      }
      return 0;
    });

    return filtered;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      confirmed: 'badge-success',
      completed: 'badge-info',
      cancelled: 'badge-danger'
    };
    return badges[status] || 'badge-info';
  };

  const renderBookingCard = (booking) => {
    const checkInDate = new Date(booking.check_in);
    const checkOutDate = new Date(booking.check_out);
    const now = new Date();

    const isCheckInToday = checkInDate.toDateString() === now.toDateString();
    const isCheckOutToday = checkOutDate.toDateString() === now.toDateString();

    return (
      <div key={booking.id} className="reservation-card">
        <div className="reservation-card-image">
          {booking.property?.images?.[0] ? (
            <PropertyThumbnail
              src={booking.property.images.find(img => img.is_primary)?.image_url ||
                   booking.property.images[0]?.image_url}
              width={400}
              height={300}
              alt={booking.property.title}
            />
          ) : (
            <div className="no-image">{t('hostReservations.noImage')}</div>
          )}
          <div className="reservation-badges">
            <span className={`badge ${getStatusBadge(booking.status)}`}>
              {t(`booking.${booking.status}`)}
            </span>
            {isCheckInToday && (
              <span className="badge badge-info">{t('hostReservations.checkInToday')}</span>
            )}
            {isCheckOutToday && (
              <span className="badge badge-info">{t('hostReservations.checkOutToday')}</span>
            )}
          </div>
        </div>

        <div className="reservation-card-content">
          <div className="reservation-header">
            <h3 className="reservation-property">{booking.property?.title}</h3>
            <div className="reservation-guest">
              <strong>{t('hostReservations.guest')}:</strong> {booking.guest?.first_name} {booking.guest?.last_name}
            </div>
          </div>

          <div className="reservation-details">
            <div className="detail-row">
              <FiCalendar size={16} />
              <span>
                {checkInDate.toLocaleDateString()} → {checkOutDate.toLocaleDateString()}
              </span>
            </div>
            <div className="detail-row">
              <FiClock size={16} />
              <span>{booking.nights} {booking.nights === 1 ? t('hostReservations.night') : t('hostReservations.nights')}</span>
            </div>
            <div className="detail-row">
              <FiUsers size={16} />
              <span>{booking.num_guests} {booking.num_guests === 1 ? t('hostReservations.guest') : t('hostReservations.guests')}</span>
            </div>
            <div className="detail-row">
              <FiDollarSign size={16} />
              <strong>R$ {booking.total_price}</strong>
            </div>
          </div>

          {booking.special_requests && (
            <div className="reservation-special-requests">
              <strong>{t('hostReservations.specialRequests')}:</strong>
              <p>{booking.special_requests}</p>
            </div>
          )}

          <div className="reservation-meta">
            <span className="reservation-date">
              {t('hostReservations.booked')}: {new Date(booking.created_at).toLocaleDateString()}
            </span>
            <span className={`payment-status ${booking.payment_status === 'completed' ? 'paid' : 'pending'}`}>
              {booking.payment_status === 'completed' ? '✓ ' : '⏱ '}
              {t(`booking.payment`)}: {booking.payment_status}
            </span>
          </div>

          <div className="reservation-actions">
            <Link to={`/bookings/${booking.id}`} className="btn btn-sm btn-secondary">
              <FiEye size={16} /> {t('hostReservations.viewDetails')}
            </Link>

            <button
              onClick={() => navigate(`/messages?userId=${booking.guest.id}`)}
              className="btn btn-sm btn-primary"
            >
              <FiMessageSquare size={16} /> {t('hostReservations.messageGuest')}
            </button>

            {booking.status === 'pending' && (
              <>
                <button
                  onClick={() => handleConfirmBooking(booking.id)}
                  className="btn btn-sm btn-success"
                >
                  <FiCheck size={16} /> {t('hostReservations.confirm')}
                </button>
                <button
                  onClick={() => handleRejectBooking(booking.id)}
                  className="btn btn-sm btn-danger"
                >
                  <FiX size={16} /> {t('hostReservations.reject')}
                </button>
              </>
            )}

            {booking.status === 'confirmed' && new Date(booking.check_out) < now && (
              <button
                onClick={() => handleCompleteBooking(booking.id)}
                className="btn btn-sm btn-success"
              >
                <FiCheck size={16} /> {t('hostReservations.complete')}
              </button>
            )}

            {(booking.status === 'confirmed' || booking.status === 'pending') && (
              <button
                onClick={() => handleCancelBooking(booking.id)}
                className="btn btn-sm btn-outline-danger"
              >
                <FiX size={16} /> {t('hostReservations.cancel')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const categorized = categorizeBookings();
  const stats = calculateStats();
  const currentBookings = categorized[activeTab] || [];
  const filteredBookings = filterAndSortBookings(currentBookings);

  return (
    <div className="host-reservations-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1>{t('hostReservations.title')}</h1>
            <p className="page-subtitle">{t('hostReservations.subtitle')}</p>
          </div>
          <Link to="/host/dashboard" className="btn btn-secondary">
            {t('hostReservations.backToDashboard')}
          </Link>
        </div>

        {/* Statistics */}
        <div className="stats-grid">
          <div className="stat-card highlight">
            <div className="stat-icon warning">
              <FiClock size={24} />
            </div>
            <div className="stat-content">
              <h3>{stats.pending}</h3>
              <p>{t('hostReservations.pendingRequests')}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon success">
              <FiCalendar size={24} />
            </div>
            <div className="stat-content">
              <h3>{stats.checkInsToday}</h3>
              <p>{t('hostReservations.checkInsToday')}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon info">
              <FiCalendar size={24} />
            </div>
            <div className="stat-content">
              <h3>{stats.checkOutsToday}</h3>
              <p>{t('hostReservations.checkOutsToday')}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon primary">
              <FiDollarSign size={24} />
            </div>
            <div className="stat-content">
              <h3>R$ {stats.monthlyRevenue.toFixed(0)}</h3>
              <p>{t('hostReservations.monthlyRevenue')}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon success">
              <FiTrendingUp size={24} />
            </div>
            <div className="stat-content">
              <h3>{stats.occupancyRate}%</h3>
              <p>{t('hostReservations.occupancyRate')}</p>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="filters-section">
          <div className="search-box">
            <FiSearch size={20} />
            <input
              type="text"
              placeholder={t('hostReservations.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <FiFilter size={20} />
            <select
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
            >
              <option value="all">{t('hostReservations.allProperties')}</option>
              {properties.map(prop => (
                <option key={prop.id} value={prop.id}>{prop.title}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="check_in">{t('hostReservations.sortByCheckIn')}</option>
              <option value="created_at">{t('hostReservations.sortByCreated')}</option>
              <option value="total_price">{t('hostReservations.sortByPrice')}</option>
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          <button
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            {t('hostReservations.pending')} ({categorized.pending.length})
            {categorized.pending.length > 0 && <span className="tab-badge">{categorized.pending.length}</span>}
          </button>
          <button
            className={`tab ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            {t('hostReservations.active')} ({categorized.active.length})
          </button>
          <button
            className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`}
            onClick={() => setActiveTab('upcoming')}
          >
            {t('hostReservations.upcoming')} ({categorized.upcoming.length})
          </button>
          <button
            className={`tab ${activeTab === 'past' ? 'active' : ''}`}
            onClick={() => setActiveTab('past')}
          >
            {t('hostReservations.past')} ({categorized.past.length})
          </button>
        </div>

        {/* Bookings Grid */}
        {filteredBookings.length === 0 ? (
          <div className="no-bookings">
            <h3>{t('hostReservations.noBookings')}</h3>
            <p>{t('hostReservations.noBookingsDesc')}</p>
          </div>
        ) : (
          <div className="reservations-grid">
            {filteredBookings.map(renderBookingCard)}
          </div>
        )}
      </div>
    </div>
  );
};

export default HostReservations;
